import { ReactNode, useState } from "react";
import AddModel from "../../components/Add/AddModel";
import AddBottomButton from "../../components/Add/AddBottomButton";
import AddInputs from "../../components/Add/AddInputs";
import TextArea from "../../components/inputfields/TextArea";
import CustomRadioButton from "../../components/buttons/CustomRadioButton";
import S3ImageUpload from "../../components/S3uploads/S3ImageUpload";
import DynamicDropdown from "../../components/Dropdowns/DynamicDropdown";
import InputLabel from "../../components/inputfields/InputLabel";
import useApi from "../../services/useApi";
import endpoints from "../../services/endpoints";
import { useDispatch } from "react-redux";
import { showToast } from "../../components/toastMessage/toastSlice";
import usePrivilege from "../../components/CustomHooks/usePrivilege";

interface AddManualEntryProps {
  children?: ReactNode;
}

const AddManualEntry = ({ children }: AddManualEntryProps) => {
  const { getAPI, PostAPI } = useApi();
  const dispatch = useDispatch();
  const isSuperAdmin = usePrivilege('superadmin');
  const [formData, setFormData] = useState({
    amount: "",
    received_from: {},
    receiver_payment_source: "Bank",
    receiver_bank: {},
    sender_payment_source: "Bank",
    sender_bank: {},
    transaction_receipt: ""
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const handleInputChange = (key: string, value: any) => {
    if (key === "amount" && typeof value === "string") {
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(value)) return;
      if (value.length > 15) return;
      const parts = value.split('.');
      if (parts[0].length > 12) return;
    }

    if (key === "receiver_payment_source") {
      setFormData(prev => ({
        ...prev,
        receiver_bank: {},
        transaction_receipt: ""
      }));
    }

    if (key === "sender_payment_source") {
      setFormData(prev => ({
        ...prev,
        sender_bank: {}
      }));
    }

    setFormData(prev => ({
      ...prev,
      [key]: value
    }));

    setErrors(prev => ({
      ...prev,
      [key]: ""
    }));
  };

  const fetchUserData = async (endpoint: string, searchTerm: string, page: number = 1) => {
    try {
      const adminParam = endpoint === 'admin' ? '&type=admin' : '&assign_fee_mode=true';
      const response = await getAPI(`${endpoint}s/?page_number=${page}${adminParam}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ""}`);
      if (!response.error) {
        return {
          data: {
            users: response.data.data.map((user: any) => ({
              id: user.id,
              user_name: user.first_name + ' ' + (user.last_name ? user.last_name : ''),
              user_code: user.user_code,
              status: user.status || "Active"
            })),
            total_count: response.data.total_count
          }
        };
      }
      return { data: { users: [], total_count: 0 } };
    } catch (error) {
      return { data: { users: [], total_count: 0 } };
    }
  };

  const baseDropdownOptions = [
    { label: "Master Distributor", value: "master_distributor", isNested: true, apiEndpoint: "master-distributor", id: "master-distributor", testId: "master-distributor" },
    { label: "Distributor", value: "distributor", isNested: true, apiEndpoint: "distributor", id: "distributor", testId: "distributor" },
    { label: "Sub Distributor", value: "sub_distributor", isNested: true, apiEndpoint: "sub-distributor", id: "sub-distributor", testId: "sub-distributor" },
    { label: "Agent", value: "agent", isNested: true, apiEndpoint: "agent", id: "agent", testId: "agent" },
    { label: "Retailer", value: "retailer", isNested: true, apiEndpoint: "retailer", id: "retailer", testId: "retailer" }
  ];

  const nestedDropdownOptions = isSuperAdmin 
    ? [{ label: "Admin", value: "admin", isNested: true, apiEndpoint: "admin", id: "admin", testId: "admin" }, ...baseDropdownOptions]
    : baseDropdownOptions;

  const handleNestedDropdownSelect = (value: any) => {
    setFormData(prev => ({
      ...prev,
      received_from: value
    }));
    setErrors(prev => ({
      ...prev,
      received_from: ""
    }));
  };



  const inputs = {
    "": {
      "Amount": {
        required: true,
        id: "amount",
        type: "input",
        tabIndex: 0,
        bottom: "mb-0"
      },
      "Received From": {
        id: "received_from",
        type: "nested_dropDown",
        required: true,
        tabIndex: 1,
        bottom: "mb-0",
        placeholder: "Select",
        testId: "received-from"
      }
    }
  };

  const { bankList, regionalOffices } = endpoints;

  const handleDynamicValue = (value: any) => {
    return value.name;
  };

  const validateForm = () => {
    const newErrors: any = {};
    
    if (!formData.amount) {
      newErrors.amount = "This field is required";
    } else if (Number(formData.amount) < 1) {
      newErrors.amount = "Amount must be at least AFA 1.00";
    }
    if (!formData.received_from?.id) newErrors.received_from = "This field is required";
    if (!formData.receiver_bank?.id) newErrors.receiver_bank = "This field is required";
    if (!formData.sender_bank?.id) newErrors.sender_bank = "This field is required";

    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getUserTypeFromCode = (userCode: string) => {
    if (!userCode) return "";
    const match = userCode.match(/^([A-Z]+)/);
    const extractedType = match ? match[1] : "";
    const allowedTypes = ['ADM', 'MDST', 'DST', 'SDST', 'AGT', 'RET'];
    return allowedTypes.includes(extractedType) ? extractedType : "";
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    console.log("Submitting manual entry with data:", formData, "and note:", note);
    console.log('Received from:', formData.received_from);
    const payload = {
      amount: parseFloat(formData.amount),
      receiver_user_type: getUserTypeFromCode(formData.received_from.user_code),
      receiver_user_id: formData.received_from.id,
      sender_payment_source: formData.sender_payment_source,
      receiver_payment_source: formData.receiver_payment_source,
      sender_source_id: formData.sender_bank.id,
      receiver_source_id: formData.receiver_bank.id,
      transaction_receipt: formData.transaction_receipt,
      note: note
    };
    
    try {
      const response = await PostAPI(`${endpoints.manualEntries}/stock-transfer`, payload);
      if (!response.error) {
        dispatch(showToast({ type: "success", message: "Manual entry added successfully" }));
        setFormData({
          amount: "",
          received_from: {},
          receiver_payment_source: "Bank",
          receiver_bank: {},
          sender_payment_source: "Bank",
          sender_bank: {},
          transaction_receipt: ""
        });
        setNote("");
        setErrors({});
        setResetKey(prev => prev + 1);
      } else {
        const errorMsg = response?.data?.response?.data?.message || "Something went wrong. Please try again.";
        dispatch(showToast({ type: "error", message: errorMsg }));
      }
    } catch (error) {
      const errorMsg = "Something went wrong. Please try again.";
      dispatch(showToast({ type: "error", message: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <AddModel>
        {children}
        
        <div className="flex flex-col mt-4">
          <AddInputs
            key={resetKey}
            mapping={true}
            inputs={inputs}
            totalInputCount={2}
            onInputChange={handleInputChange}
            inputValues={formData}
            errors={errors}
            nestedDropdownOptions={nestedDropdownOptions}
            handleNestedDropdownSelect={handleNestedDropdownSelect}
            fetchUserData={fetchUserData}
          />
        </div>

        <div className="w-full flex flex-col sm:flex-row pb-4 gap-4 sm:gap-x-6 mt-6">
          <div className="bg-background_blue w-full sm:w-[50%] rounded-lg p-4">
            <InputLabel
              labelName={"Receiver Payment Source"}
              id={"receiver_payment_source"}
              required={true}
            />
            <CustomRadioButton
              options={[
                { id: "Bank", label: "Bank" },
                { id: "Cash", label: "Cash" }
              ]}
              selected={formData?.receiver_payment_source}
              onChange={(value: string) =>
                handleInputChange("receiver_payment_source", value)
              }
              gapClassName={"gap-x-[40px]"}
              padding="py-1"
            />
            <div className="pt-4">
              <DynamicDropdown
                apiEndPoint={`${formData.receiver_payment_source === 'Cash' ? regionalOffices : bankList}?${formData?.received_from?.id ? `entity_id=${formData.received_from.id}&` : ''}page_number=`}
                labelName={""}
                id={"receiver_bank"}
                tabIndex={1}
                noSearch={false}
                handelDynamicValue={handleDynamicValue}
                onChange={handleInputChange}
                selectedValue={formData?.receiver_bank}
                errorMessage={errors?.receiver_bank}
                dropdownTopClass={"top-[65px]"}
                toolTipClassName={"-top-[1rem]"}
                disabled={!formData?.received_from?.id}
                className={!formData?.received_from?.id ? 'cursor-not-allowed' : ''}
                placeHolderValue={
                  formData.receiver_payment_source === "Cash"
                    ? "regional office"
                    : "bank"
                }
              />
            </div>
            {(Object.keys(formData?.receiver_bank).length !== 0 && Object.keys(formData?.receiver_bank) && formData?.receiver_bank?.charge_mode) && (
              <p className="text-body2 text-secondary_text mt-1">
                Note:{" "}
                {`${
                  formData?.receiver_bank?.charge_mode === "PERCENTAGE"
                    ? `${formData?.receiver_bank?.charge_value}% bank charges associated`
                    : ` Bank charges of AFA ${formData?.receiver_bank?.charge_value} will be deducted.`
                }`}
              </p>
            )}
            <S3ImageUpload
              heading=""
              label={`${formData.receiver_payment_source === 'Cash' ? 'Cash' : 'Bank'} Transfer Receipt`}
              disabled={false}
              upload_id={"transaction_receipt"}
              UploadPath={"uploads/transaction_receipt"}
              handleUpload={handleInputChange}
              uploadedFile={formData.transaction_receipt || ""}
              errorMessage={errors?.transaction_receipt}
              required={false}
              paddingTop="pt-4"
            />
          </div>
          <div className="bg-background_blue w-full sm:w-[50%] rounded-lg p-4">
            <InputLabel
              labelName={"Sender Payment Source"}
              id={"sender_payment_source"}
              required={true}
            />
            <CustomRadioButton
              options={[
                { id: "Bank", label: "Bank" },
                { id: "Cash", label: "Cash" }
              ]}
              selected={formData?.sender_payment_source}
              onChange={(value: string) =>
                handleInputChange("sender_payment_source", value)
              }
              gapClassName={"gap-x-[40px]"}
              padding="py-1"
            />
            <div className="pt-4">
              <DynamicDropdown
                apiEndPoint={`${formData.sender_payment_source === 'Cash' ? regionalOffices : bankList}?page_number=`}
                labelName={""}
                id={"sender_bank"}
                tabIndex={1}
                noSearch={false}
                handelDynamicValue={handleDynamicValue}
                onChange={handleInputChange}
                selectedValue={formData?.sender_bank}
                errorMessage={errors?.sender_bank}
                dropdownTopClass={"top-[65px]"}
                toolTipClassName={"-top-[1rem]"}
                placeHolderValue={
                  formData.sender_payment_source === "Cash"
                    ? "regional office"
                    : "bank"
                }
              />
            </div>
          </div>
        </div>

        <div className="pt-4">
          <TextArea
            top="pt-0"
            required={false}
            label="Note"
            text={note}
            setText={setNote}
            textError={noteError}
            setTextError={setNoteError}
            maxLength={250}
            placeholderRequied={false}
          />
        </div>
      </AddModel>

      <AddBottomButton
        buttonWidth="w-[140px]"
        buttonName="Submit"
        buttonClass="w-[140px] whitespace-nowrap cursor-pointer"
        onClick={handleSubmit}
        disabled={false}
        loading={loading}
      />
    </div>
  );
};

export default AddManualEntry;